/**
 * AI-credits wallet store — a small, shell-wide source of truth for the
 * tenant's balance, shared by the header badge ({@link AiCreditsBadge}) and the
 * global refill host ({@link CreditGuardHost}).
 *
 * Kept separate from the heavier settings-store (which also loads the ledger +
 * usage) so the header widget can show a live balance without pulling the whole
 * settings surface. `refresh()` hits `/api/v1/credits/balance`, which
 * auto-provisions the wallet + welcome grant server-side, and is safe to call
 * on mount, on a poll, and after any AI action.
 */
import { create } from 'zustand';

import { getBalance, isUnlimitedWallet, type Wallet } from '@/lib/api/credits';
import { getMySubscription } from '@/lib/api/subscription';

/** Window event any component can fire to ask the badge/host to re-fetch. */
export const AI_CREDITS_UPDATED_EVENT = 'psn:ai-credits-updated';

/** This billing cycle's allowance / consumption (for the "% used" meter). */
export interface CreditCycle {
  allowance: number; // plan.ai_credits_monthly (monthly grant)
  used: number; // credits consumed this cycle
  pctUsed: number; // 0-100, clamped
  resetDate: string | null; // current_period_end (ISO)
}

/** Fire-and-forget: ask the credits store to refresh (e.g. after an AI run). */
export function requestCreditsRefresh(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(AI_CREDITS_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

interface CreditsState {
  wallet: Wallet | null;
  cycle: CreditCycle | null;
  loading: boolean;
  loaded: boolean;
  /** Guards against overlapping in-flight refreshes. */
  _inflight: boolean;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
  /** Refresh the billing-cycle allowance/usage (for the "% used" meter). */
  refreshCycle: () => Promise<void>;
  /**
   * Apply a server-reported post-settle balance immediately (no round-trip), so
   * the header badge drops the moment an AI action completes. The follow-up
   * `refresh()` then reconciles with the authoritative wallet (reserved etc.).
   */
  applyBalanceAfter: (balanceAfter: number) => void;
  /** Low balance = available at or below the wallet's own alert threshold. */
  isLow: () => boolean;
}

export const useCreditsStore = create<CreditsState>((set, get) => ({
  wallet: null,
  cycle: null,
  loading: false,
  loaded: false,
  _inflight: false,

  refresh: async (opts) => {
    if (get()._inflight && !opts?.force) return;
    set({ _inflight: true, loading: !get().loaded });
    try {
      const wallet = await getBalance();
      set({ wallet, loaded: true });
    } catch {
      // The wallet service may be unreachable (a fresh env / outage). Degrade
      // silently — the badge hides and the guard host still shows the 402 modal
      // from the event payload, which carries required/available on its own.
      set({ loaded: true });
    } finally {
      set({ _inflight: false, loading: false });
    }
    // Cycle allowance is cheaper to refresh alongside the balance (best-effort).
    void get().refreshCycle();
  },

  refreshCycle: async () => {
    try {
      const me = await getMySubscription();
      const ai = me.usage?.ai_credits;
      const allowance = me.plan?.ai_credits_monthly ?? ai?.limit ?? 0;
      const used = ai?.used ?? 0;
      const pctUsed = allowance > 0 ? Math.min(100, Math.max(0, (used / allowance) * 100)) : 0;
      set({
        cycle: {
          allowance,
          used,
          pctUsed,
          resetDate: me.subscription?.current_period_end ?? null,
        },
      });
    } catch {
      // Subscription service may be unreachable — the badge falls back to the
      // raw balance without the "% used" adornment.
    }
  },

  applyBalanceAfter: (balanceAfter) => {
    const w = get().wallet;
    if (!w || isUnlimitedWallet(w) || !Number.isFinite(balanceAfter)) return;
    const reserved = w.reserved ?? 0;
    set({
      wallet: {
        ...w,
        balance: balanceAfter,
        available: Math.max(0, Math.round((balanceAfter - reserved) * 1e6) / 1e6),
      },
    });
  },

  isLow: () => {
    const w = get().wallet;
    if (!w) return false;
    // Unlimited (superadmin) is never "low" — there is no finite balance.
    if (isUnlimitedWallet(w)) return false;
    const threshold = w.low_balance_threshold ?? 0;
    return (w.available ?? 0) <= threshold;
  },
}));
